import type { AddOn } from "schemas/generated/addOns";
import type {
  BillingInterval,
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { formatPrice } from "replo-utils/lib/math";

import {
  getActiveSubscriptionPricePerMonth,
  getEstimatedPlanPricePerMonth,
} from "./planPriceUtils";

type PlanPriceVariant = "base" | "subheader" | "header";

type EstimatedPlanPricePerMonthProps = {
  billingTier: BillingTier;
  billingInterval: BillingInterval;
  addOns: AddOn[];
  variant: PlanPriceVariant;
};

/**
 * Displays the estimated price per month of the selected plan and add-ons.
 */
export const EstimatedPlanPricePerMonth = (
  props: EstimatedPlanPricePerMonthProps,
) => {
  const priceToDisplay = getEstimatedPlanPricePerMonth({
    billingTier: props.billingTier,
    billingInterval: props.billingInterval,
    addOns: props.addOns,
  });

  return (
    <BasePlanPricePerMonth
      priceToDisplay={priceToDisplay}
      variant={props.variant}
    />
  );
};

/**
 * Displays the actual price per month of the currently active subscription based on amount paid from the db.
 */
export const ActiveSubscriptionPricePerMonth = ({
  activeSubscription,
  variant,
}: {
  activeSubscription: SubscriptionDetails;
  variant: PlanPriceVariant;
}) => {
  const priceToDisplay = getActiveSubscriptionPricePerMonth({
    activeSubscription,
  });

  return (
    <BasePlanPricePerMonth priceToDisplay={priceToDisplay} variant={variant} />
  );
};

const BasePlanPricePerMonth = ({
  priceToDisplay,
  variant,
}: {
  variant: PlanPriceVariant;
  priceToDisplay: number;
}) => {
  const formattedPrice = formatPrice(priceToDisplay);

  const variantClassName: Record<PlanPriceVariant, string> = {
    base: "text-base font-medium",
    subheader: "header-h3",
    header: "text-[28px] font-semibold flex items-center gap-1",
  };

  const suffixClassName: Record<PlanPriceVariant, string> = {
    base: "text-base font-medium",
    subheader: "text-xs",
    header: "text-sm",
  };

  return (
    <span className={twMerge(variantClassName[variant])}>
      {formattedPrice}
      <span className={twMerge("font-normal", suffixClassName[variant])}>
        /month
      </span>
    </span>
  );
};
